import React, { useEffect, useState, useCallback } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import FileSaver from 'file-saver';

import { dataActions } from '@/store/actions/data.actions';

import api from '@/services/api';

import { getUrls } from '@/utils/urls/getUrls';
import useWindowIsMobile from '@/utils/hooks/useWindowIsMobile';
import Tab from '@/views/components/Tab/Tab';
import Header from '@/views/components/Header/Header';
import Footer from '@/views/components/Footer/Footer';
import TemplateCreateHeader from '@/views/pages/Template/elements/TemplateCreateHeader';
import TemplateCreateLetterInfo from '@/views/pages/Template/elements/TemplateCreateLetterInfo';
import TemplateInfo from '@/views/pages/Template/elements/TemplateInfo';
import TemplateCreateLetterWrapper from '@/views/pages/Template/elements/TemplateCreateLetterWrapper';
import TemplateCreateFormWrapper from '@/views/pages/Template/elements/TemplateCreateFormWrapper';
import TemplateCreateForm from '@/views/pages/Template/elements/TemplateCreateForm';
import TemplateUseFormWrapper from '@/views/pages/Template/elements/TemplateUseFormWrapper';

import TemplateUseForm from '@/views/pages/Template/elements/TemplateUseForm';

import Spinner from '@/views/components/Spinner/Spinner';

import FlexWrapper from '@/utils/FlexWrapper';

import styles from '@/views/pages/Template/Template.module.scss';

export const templateComponentModes = {
	editMode: 'EDIT_MODE',
	createMode: 'CREATE_MODE',
	useMode: 'USE_MODE',
};

const formDefaultValues = {
	category: null,
	templateName: '',
	baseForCategory: false,
	departmentId: null,
	footerId: null,
	headerId: null,
	// department: { id: null },
	// footer: { id: null },
	// header: { id: null },

	// content
	letterTitle: '',
	letterContent: '',
	regulations: '',

	// imprint:
	// name: false,
	// address: false,
	// contact: false,
	// typeOfCase: false,
	// caseReference: false,
	// createdOn: false,
};

const processInitialValues = (templateValues) => {
	const { content: letterTitle } = { ...templateValues.contentFields.find(item => item.type.code === 'content-letter-title') };
	const { content: letterText } = { ...templateValues.contentFields.find(item => item.type.code === 'content-letter-text') };
	const { content: letterRights } = { ...templateValues.contentFields.find(item => item.type.code === 'content-letter-rights') };
	return ({
		category: templateValues.category,
		templateName: templateValues.title,
		baseForCategory: templateValues.baseForCategory,
		departmentId: templateValues.department.id,
		footerId: templateValues.footer.department.id,
		headerId: templateValues.header.department.id,
		letterTitle: letterTitle ?? '',
		letterContent: letterText ?? '',
		regulations: letterRights ?? '',
	});
};

const Template = ({
	mode = templateComponentModes.useMode,
}) => {
	// if edit mode - get data of Template (by id in url)
	const dispatch = useDispatch();
	const { id: urlId } = useParams();
	const navigate = useNavigate();
	const { categories } = useSelector(state => state.data);
	const { user } = useSelector((state) => state.auth);
	const { dict } = useSelector((state) => state.app);

	const isMobile = useWindowIsMobile(991);
	const [ mobileViewOpen, setMobileViewOpen ] = useState('form');

	const [ letterInfo, setLetterInfo ] = useState(null);

	const { letters } = getUrls();

	// only for edit purpose:
	const [ templateData, setTemplateData ] = useState(null);

	const [ letterContents, setLetterContents ] = useState(null);

	const getProperLetterContent = (letterMode) => letterMode === 'finalDoc';
	const templateModes = letterContents && Object.keys(letterContents).filter((key) => getProperLetterContent(key)).map((key) => ({mode: key, content: letterContents[key]}));
	const availableModes = templateModes && templateModes.filter((availableMode) => availableMode.content);

	const [ sendingData, setSendingData ] = useState(false);

	const [ metricItems, setMetricItems ] = useState(null);
	const [ formInitialValues, setFormInitialvalues ] = useState(null);

	const [ selectCategories, setSelectCategories ] = useState(null);

	useEffect(() => {
		if (!categories) dispatch(dataActions.getLetterCategories());
		if (!urlId) return;
		switch (mode) {
			case 'EDIT_MODE':
				startEditMode(urlId);
				break;
			case 'CREATE_MODE':
				startCreateMode(urlId);
				break;
			case 'USE_MODE':
				startUseTemplateMode(urlId);
				break;
			default:
				startUseTemplateMode(urlId);
				break;
		}
	}, [ urlId ]);

	const processLetterContents = (content) => {
		let processedContent = content.replaceAll(/<a[^>]*>/g, '[').replaceAll('</a>', ']');
		return processedContent;
	};

	const startCreateMode = async (idUrl) => {
		// CREATE MODE
		await getLinkedToTemplateLetterData(idUrl);

		// get metric field types for Form
		const { data: tempMetricItems } = await api.get('/template-field-types/metric');
		const reducedTempMetricData = tempMetricItems.reduce((agr, item) => ({ ...agr, [item.code]: false }), {});
		setMetricItems(tempMetricItems);

		const contents = await getLetterContents(idUrl);

		const rawFootnotes = await getRawFootnotes(idUrl);

		let processedFootnotes = [];
		for (let i = 0; i < rawFootnotes.length; ++i) {
			let footnoteWords = rawFootnotes[i].split(" ");
			processedFootnotes.push(`[${footnoteWords[0]}] ${ footnoteWords.slice(1).join(" ") }`);
		}

		setFormInitialvalues({ 
			...formDefaultValues, 
			letterContent: processLetterContents(contents.finalDoc.content),
			regulations: processedFootnotes.join("<br />"),
			departmentId:user?.departments[0]?.id,
			...reducedTempMetricData,
		});
	};

	const startEditMode = async (idUrl) => {
		// EDIT MODE
		const { data: template } = await api.get(`/letter-templates/${idUrl}`);
		const letterId = template.letter.id;

		await getLinkedToTemplateLetterData(letterId);

		// to know which fields to show in metrics:
		const { data: tempMetricItems } = await api.get('/template-field-types/metric');
		const reducedTempMetricData = tempMetricItems.reduce((agr, item) => {
			return ({ ...agr, [item.code]: !!(template.metricFields.find(field => field.type.code === item.code)) });
		}, {});
		setMetricItems(tempMetricItems);
		setFormInitialvalues({ ...processInitialValues(template), ...reducedTempMetricData });
		setTemplateData(template);

		getLetterContents(letterId);
	};

	const startUseTemplateMode = async (idUrl) => {
		// USE MODE
		const { data: template } = await api.get(`/letter-templates/${idUrl}`);
		const { id: letterId } = template.letter;
		setFormInitialvalues(template);
		setTemplateData(template);

		await getLinkedToTemplateLetterData(letterId);

		await getLetterContents(letterId);
	};

	const getLinkedToTemplateLetterData = async (letterId) => {
		// get data of letter linked to edited template:
		const { data: letterInfoAdditional } = await api.get(`/letters/${letterId}`);
		setLetterInfo(letterInfoAdditional);
		return;
	};

	const getLetterContents = async (letterId) => {
		const { data: letterContentsData } = await api.get(`/texts/${letterId}/`);
		setLetterContents(letterContentsData);
		return letterContentsData;
	};

	const getRawFootnotes = async (letterId) => {
		const { data: rawFootnotes } = await api.get(`/texts/${letterId}/raw-footnotes`);
		return rawFootnotes;
	};

	useEffect(() => {
		if (categories) {
			setSelectCategories(categories.map(item => ({
				value: item.code, label: item.label,
			})));
		}
		// eslint-disable-next-line
    }, [categories]);

	const formatContent = (content) => {
		const maxLoops = 1000;
		let currentLoop = 0;
		let currentIndex = 0;
		let currentContent = content;

		while (currentLoop < maxLoops) {
			let headerStart = currentContent.indexOf('<h2>', currentIndex);

			if (headerStart === -1) {
				break;
			} else {
				let headerEnd = currentContent.indexOf('</h2>', headerStart);
				let brTag = currentContent.indexOf('<br>', headerStart);
				if (brTag > headerStart && brTag < headerEnd) {
					currentContent = currentContent.substring(0, brTag) + currentContent.substring(brTag + 4);
					currentIndex = headerStart;
				} else {
					currentIndex = headerEnd;
				}
			}
			currentLoop++;
		}

		currentLoop = 0;
		currentIndex = 0;

		while (currentLoop < maxLoops) {
			let headerStart = currentContent.indexOf('<p>', currentIndex);

			if (headerStart === -1) {
				break;
			} else {
				let headerEnd = currentContent.indexOf('</p>', headerStart);
				let brTag = currentContent.indexOf('<br>', headerStart);
				if (brTag > headerStart && brTag < headerEnd) {
					currentContent = currentContent.substring(0, brTag) + currentContent.substring(brTag + 4);
					currentIndex = headerStart;
				} else {
					currentIndex = headerEnd;
				}
			}
			currentLoop++;
		}
		
		currentContent = currentContent.replace(/&nbsp;/g, " ");

		return currentContent;
	};

	const uploadTemplateData = useCallback((data) => {
		if (sendingData) return;
		if (mode === 'EDIT_MODE') {
			setSendingData(true);
			return api.put(`/letter-templates/${urlId}`, data)
				.then(() => {
					navigate(`/templates?page=1`);
				}).catch(() => {
					setSendingData(false);
				});
		}
		if (mode === 'USE_MODE') {
			const retObject = {
				contentFields: [ ...templateData.contentFields.map(item => {
					if(data[item.type.code] && item.type.code === 'content-letter-text') {
						return { ...item, content: formatContent(data[item.type.code]) };
					}
					if(data[item.type.code] && item.type.code === 'content-letter-rights'){
						return { ...item, content: data[item.type.code].replace(/&nbsp;/g, " ")};
					}
					if (data[item.type.code]) {
						return { ...item, content: data[item.type.code] };
					}
					return item;
				}) ],
				metricFields: [ ...templateData.metricFields.map(item => {
					if (data[item.type.code]) {
						return { ...item, content: data[item.type.code] };
					}
					return item;
				}) ],
			};

			setSendingData(true);

			return api.post(`letter-templates/${templateData.id}/docx`, {
				...retObject,
			}, { responseType: 'blob' })
				.then((response) => {
					let fileName = response.headers["content-disposition"].split("filename=")[1];
					fileName = fileName.replaceAll('"','');
					FileSaver.saveAs(response.data, fileName);
					setSendingData(false);
				}).catch(() => {
					setSendingData(false);
				});
		}
		api.post(`/letter-templates`, data)
			.then(() => {
				// setSendingData(false);
				navigate(`${letters}?page=1`);
			}).catch(() => {
				setSendingData(false);
				// setLoading(false);
				// onFormErrors(response);
			});
	}, [ mode, templateData, sendingData ]);

	const templateUseProcessInitialValues = useCallback((initVals) => {
		const processedContentFields = initVals.contentFields.reduce((agr, item) => {
			return ({ ...agr, [item.type.code]: item.content });
		}, {});
		const processedMetricFields = initVals.metricFields.reduce((agr, item) => {
			return ({ ...agr, [item.type.code]: item.content });
		}, {});
		return { ...processedContentFields, ...processedMetricFields };
	}, []);

	return (
		<FlexWrapper>
			<Header narrow />
			{mode === 'CREATE_MODE' && letterInfo && letterInfo.title && (
				<TemplateCreateHeader
					title={ letterInfo.title }
					mode={ mode }
					letterData={ letterInfo }
				/>
			)}
			
			{mode !== 'CREATE_MODE' && letterInfo && templateData && templateData.title && (
				<TemplateCreateHeader
					title={ mode === 'USE_MODE' ? templateData.title : letterInfo.title }
					mode={ mode }
					letterData={ templateData.letter }
					templateData={ templateData }
				/>
			)}

			{mode === 'CREATE_MODE' && letterInfo && (
				<TemplateCreateLetterInfo
					letterData={ letterInfo }
				/>
			)}

			{mode !== 'CREATE_MODE' && templateData && (
				<TemplateInfo
					templateData={ { ...formInitialValues, ...templateData } }
				/>
			)}

			{isMobile && mode !== 'USE_MODE' &&  (
				<div className={ styles['template-mobile-option'] }>
					<Tab
						isBtn={ true }
						isTabActive={ mobileViewOpen === 'lookup' }
						onClickHandler={ () => { setMobileViewOpen('lookup'); } }
						value='createFormMode'
						label={ dict.templates.create.letterLookup }
						style={ { borderRight: 'none' } }
					/>
					<Tab
						isBtn={ true }
						isTabActive={ mobileViewOpen === 'form' }
						onClickHandler={ () => { setMobileViewOpen('form'); } }
						value='createFormMode'
						label={ mode === 'EDIT_MODE' ? dict.templates.create.updateTemplateHeader : dict.templates.create.createTemplateHeader }
						style={ { borderRight: 'none' } }
					/>
				</div>
			)}
			<section className='letter__versions'>
				{mode !== 'USE_MODE' && <>
					{isMobile ? (
						<>
							{mobileViewOpen === 'lookup' && <TemplateCreateLetterWrapper
								availableModes={ availableModes }
							/>}
							{mobileViewOpen === 'form' && <TemplateCreateFormWrapper mode={ mode }>
								{!formInitialValues && (
									<div style={ { display: 'flex' } }>
										<Spinner />
									</div>
								)}
								{sendingData && (
									<div className={ styles['template-spinner'] }>
										<Spinner />
									</div>
								)}
								{formInitialValues && (
									<TemplateCreateForm
										formInitialValues={ formInitialValues }
										metricItems={ metricItems }
										selectCategories={ selectCategories }
										uploadTemplateData={ uploadTemplateData }
										editMode={ mode === 'EDIT_MODE' }
										templateData={ templateData }
									/>
								)}
							</TemplateCreateFormWrapper>}
						</>
					) : (
						<>
							<TemplateCreateLetterWrapper
								availableModes={ availableModes }
							/>
							<TemplateCreateFormWrapper mode={ mode }>
								{!formInitialValues && (
									<div style={ { display: 'flex' } }>
										<Spinner />
									</div>
								)}
								{sendingData && (
									<div className={ styles['template-spinner'] }>
										<Spinner />
									</div>
								)}
								{formInitialValues && (
									<TemplateCreateForm
										formInitialValues={ formInitialValues }
										metricItems={ metricItems }
										selectCategories={ selectCategories }
										uploadTemplateData={ uploadTemplateData }
										editMode={ mode === 'EDIT_MODE' }
										templateData={ templateData }
									/>
								)}
							</TemplateCreateFormWrapper>
						</>
					)}
				</>}
				{mode === 'USE_MODE' && (
					formInitialValues && (
						<TemplateUseFormWrapper templateData={ templateData }>
							<TemplateUseForm
								formInitialValues={ templateUseProcessInitialValues(formInitialValues) }
								entireData={ formInitialValues }
								uploadTemplateData={ uploadTemplateData }
							/>
						</TemplateUseFormWrapper>
					)
				)}
			</section>
			<Footer 
				narrow 
				className={ styles['template-footer'] }
			/>
		</FlexWrapper>
	);
};
export default Template;
